import { useState, useEffect } from 'react';
import { Plus, Trash2, LogOut, LogIn, Loader2, AlertCircle, ExternalLink, UserPlus } from 'lucide-react';

// Vercel sets REACT_APP_API_BASE; empty default = same-origin (self-hosted build)
const API_BASE = process.env.REACT_APP_API_BASE || '';

export default function CMS() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [filterYear, setFilterYear] = useState('');
  const [filterTrimester, setFilterTrimester] = useState('');
  
  const [formData, setFormData] = useState({
    year: 'D1',
    trimester: '1',
    className: '',
    guideName: '',
    link: ''
  });
  
  const [newUserData, setNewUserData] = useState({
    username: '',
    password: ''
  });
  
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [selectedEntries, setSelectedEntries] = useState([]);

  useEffect(() => {
    const savedToken = localStorage.getItem('auth_token');
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchAllData();
    }
  }, [isAuthenticated, token]);

  const isSelected = (entry) => {
    return selectedEntries.some(
      (e) =>
        e.year === entry.year &&
        e.trimester === entry.trimester &&
        e.className === entry.className &&
        e.guideName === entry.guideName
    );
  };

  const handleRowClick = (entry) => {
    if (isSelected(entry)) {
      setSelectedEntries(selectedEntries.filter(
        (e) =>
          !(
            e.year === entry.year &&
            e.trimester === entry.trimester &&
            e.className === entry.className &&
            e.guideName === entry.guideName
          )
      ));
    } else {
      setSelectedEntries([...selectedEntries, entry]);
    }
  };

  const handleLogin = async () => {
    setLoading(true);
    setLoginError('');
    
    try {
      const response = await fetch(`${API_BASE}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password })
      });

      if (!response.ok) {
        throw new Error('Invalid credentials');
      }

      const data = await response.json();
      setToken(data.access_token);
      localStorage.setItem('auth_token', data.access_token);
      setIsAuthenticated(true);
      setLoginError('');
    } catch (err) {
      console.error('Login error:', err);
      setLoginError('Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setToken(null);
    setUsername('');
    setPassword('');
    localStorage.removeItem('auth_token');
  };

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/values`, {
        method: 'GET',
        headers: {
          'accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }

      const data = await response.json();
      setAllData(data);
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Error loading data from database.');
    } finally {
      setLoading(false);
    }
  };

  const handleInsert = async () => {
    if (!formData.className || !formData.guideName || !formData.link) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const url = `${API_BASE}/insert?Year=${formData.year}&Trimester=${formData.trimester}&Class=${encodeURIComponent(formData.className)}&Guide=${encodeURIComponent(formData.guideName)}&Link=${encodeURIComponent(formData.link)}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Session expired. Please login again.');
          handleLogout();
          return;
        }
        throw new Error('Failed to insert data');
      }

      setSuccess('Study guide added successfully!');
      setFormData({
        year: 'D1',
        className: '1',
        guideName: '',
        link: ''
      });
      
      await fetchAllData();
    } catch (err) {
      console.error('Insert error:', err);
      setError('Error adding study guide. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (selectedEntries.length === 0) {
      setError('Please select at least one entry to delete');
      return;
    }

    if (!window.confirm(`Delete ${selectedEntries.length} study guide(s)?`)) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      for (const entry of selectedEntries) {
        const url = `${API_BASE}/delete?Year=${entry.year}&Trimester=${entry.trimester}&Class=${encodeURIComponent(entry.className)}&Guide=${encodeURIComponent(entry.guideName)}`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            setError('Session expired. Please login again.');
            handleLogout();
            return;
          }
          throw new Error(`Failed to delete ${entry.guideName}`);
        }
      }

      setSuccess(`Deleted ${selectedEntries.length} study guide(s) successfully!`);
      setSelectedEntries([]);
      await fetchAllData();
    } catch (err) {
      console.error('Delete error:', err);
      setError('Error deleting study guides. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  const handleCreateUser = async () => {
    if (!newUserData.username || !newUserData.password) {
      setError('Please fill in all fields for new user');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${API_BASE}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newUserData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Session expired. Please login again.');
          handleLogout();
          return;
        }
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create user');
      }

      setSuccess(`User '${newUserData.username}' created successfully!`);
      setNewUserData({ username: '', password: '' });
      setShowCreateUser(false);
    } catch (err) {
      console.error('Create user error:', err);
      setError(err.message || 'Error creating user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <LogIn className="w-12 h-12 mx-auto mb-4 text-indigo-600" />
            <h2 className="text-3xl font-bold text-gray-800 mb-2">CMS Login</h2>
            <p className="text-gray-600">Sign in to manage study guides</p>
          </div>

          {loginError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-700">{loginError}</p>
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <button
              onClick={handleLogin}
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </div>

          <div className="mt-6 text-center">
            <a
              href="/"
              className="text-indigo-600 hover:text-indigo-700 text-sm"
            >
              ← Back to Study Guides
            </a>
          </div>
        </div>
      </div>
    );
  }

  const flattenedData = [];
  allData.forEach((yearEntry) => {
    const year = yearEntry.Year;
    Object.entries(yearEntry.Trimesters).forEach(([trimester, classes]) => {
      Object.entries(classes).forEach(([className, guides]) => {
        Object.entries(guides).forEach(([guideName, link]) => {
          flattenedData.push({ year, trimester, className, guideName, link });
        });
      });
    });
  });

  const filteredData = flattenedData.filter((entry) => {
    return (
      (filterYear === '' || entry.year === filterYear) &&
      (filterTrimester === '' || entry.trimester === filterTrimester)
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-6xl mx-auto py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800">Content Management System</h1>
          <div className="flex gap-2">
            <button
              onClick={() => setShowCreateUser(!showCreateUser)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <UserPlus className="w-5 h-5" />
              New User
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-green-700">{success}</p>
          </div>
        )}

        {showCreateUser && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <UserPlus className="w-6 h-6" />
              Create New Admin User
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={newUserData.username}
                  onChange={(e) => setNewUserData({...newUserData, username: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={newUserData.password}
                  onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div className="md:col-span-2 flex gap-2">
                <button
                  onClick={handleCreateUser}
                  disabled={loading}
                  className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <UserPlus className="w-5 h-5" />}
                  Create User
                </button>
                <button
                  onClick={() => {
                    setShowCreateUser(false);
                    setNewUserData({ username: '', password: '' });
                  }}
                  className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Plus className="w-6 h-6" />
            Add New Study Guide
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Year
              </label>
              <select
                value={formData.year}
                onChange={(e) => setFormData({...formData, year: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="D1">D1</option>
                <option value="D2">D2</option>
                <option value="D3">D3</option>
                <option value="D4">D4</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trimester
              </label>
              <select
                value={formData.trimester}
                onChange={(e) => setFormData({...formData, trimester: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="1">Trimester 1</option>
                <option value="2">Trimester 2</option>
                <option value="3">Trimester 3</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Class Name
              </label>
              <input
                type="text"
                value={formData.className}
                onChange={(e) => setFormData({...formData, className: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Guide Name
              </label>
              <input
                type="text"
                value={formData.guideName}
                onChange={(e) => setFormData({...formData, guideName: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Link
              </label>
              <input
                type="url"
                value={formData.link}
                onChange={(e) => setFormData({...formData, link: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="https://..."
              />
            </div>

            <div className="md:col-span-2">
              <button
                onClick={handleInsert}
                disabled={loading}
                className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
                Add Study Guide
              </button>
            </div>
          </div>
        </div>

                <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
            <h2 className="text-2xl font-bold text-gray-800">All Study Guides</h2>

            <div className="flex flex-wrap gap-2 items-center">
              <select
                onChange={(e) => setFilterYear(e.target.value)}
                value={filterYear}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">All Years</option>
                <option value="D1">D1</option>
                <option value="D2">D2</option>
                <option value="D3">D3</option>
                <option value="D4">D4</option>
              </select>

              <select
                onChange={(e) => setFilterTrimester(e.target.value)}
                value={filterTrimester}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">All Trimesters</option>
                <option value="1">Trimester 1</option>
                <option value="2">Trimester 2</option>
                <option value="3">Trimester 3</option>
              </select>

              {selectedEntries.length > 0 && (
                <button
                  onClick={handleDelete}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-5 h-5" />
                  Delete Selected
                </button>
              )}
            </div>
          </div>

          {loading && !flattenedData.length ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-indigo-600" />
              <p className="text-gray-600">Loading data...</p>
            </div>
          ) : filteredData.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No study guides found for this selection.</p>
          ) : (
            <div className="overflow-x-auto">
              {/* Scrollable table body */}
              <div className="max-h-[500px] overflow-y-auto border rounded-lg">
                <table className="w-full">
                  <thead className="sticky top-0 bg-white shadow-sm z-10">
                    <tr className="border-b-2 border-gray-200">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Select</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Year</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Trimester</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Class</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Guide</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((entry, index) => (
                      <tr
                        key={index}
                        className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${
                          isSelected(entry) ? 'bg-indigo-50' : ''
                        }`}
                        onClick={() => handleRowClick(entry)}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected(entry)}
                            onChange={() => handleRowClick(entry)}
                            className="w-4 h-4 text-indigo-600"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-800">{entry.year}</td>
                        <td className="px-4 py-3 text-sm text-gray-800">{entry.trimester}</td>
                        <td className="px-4 py-3 text-sm text-gray-800">{entry.className}</td>
                        <td className="px-4 py-3 text-sm text-gray-800">{entry.guideName}</td>
                        <td className="px-4 py-3 text-sm">
                          <a
                            href={entry.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            View <ExternalLink className="w-4 h-4" />
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}